import { useEffect, useState } from "react";

const QUERY = "(prefers-reduced-motion: reduce)";

/**
 * Tracks the viewer's OS-level "reduce motion" preference (Windows: Settings >
 * Ease of Access > Display > "Show animations in Windows").
 *
 * Live, not read-once: the media query is subscribed to, so toggling the setting
 * — or DevTools > Rendering > "Emulate prefers-reduced-motion" — updates the app
 * without a reload. That is what makes the effect testable in-house.
 *
 * `matchMedia` is guarded because it is absent in jsdom under `react-scripts
 * test`, where every component that mounts App would otherwise throw.
 */
const usePrefersReducedMotion = (): boolean => {
	const [prefersReducedMotion, setPrefersReducedMotion] = useState(
		() => window.matchMedia?.(QUERY).matches ?? false,
	);

	useEffect(() => {
		const mediaQuery = window.matchMedia?.(QUERY);
		if (!mediaQuery) return undefined;

		const handleChange = (event: MediaQueryListEvent) =>
			setPrefersReducedMotion(event.matches);

		mediaQuery.addEventListener("change", handleChange);
		return () => mediaQuery.removeEventListener("change", handleChange);
	}, []);

	return prefersReducedMotion;
};

export default usePrefersReducedMotion;
